import { log } from '@/log'
import { HlAppMode } from '@/models/hl-app/hl-app-mode'
import { ApiClient } from '@/services/api/client'
import { SessionApiService } from '@/services/api/session'
import { SessionApiErrorResponse } from '@/services/api/session/response'
import { SessionCookie, SessionCookiePayload } from '@/services/session/session-cookie'

const namespace = 'sync-session'

// new/keep成功直後のセッションcookie読み取りリトライの間隔と回数
// 間隔はローカルのdocument.cookie読み取りのため負荷を気にする必要がなく、検知遅延最小を優先して短い固定値
// 上限まで待つ時間は間隔×回数=約200msがめやすだが、タイマーのスケジューリング遅延の分それより長くなりうる
const READ_PAYLOAD_RETRY_INTERVAL_MS = 10
const READ_PAYLOAD_RETRY_LIMIT = 20

const waitFor = (ms: number): Promise<void> => new Promise<void>(resolve => setTimeout(resolve, ms))

/**
 * client側のconsoleログは収集できていないため、サーバのロギングAPIへ明示的に送る。
 *
 * LoggingService(ApiClientWithAuth)を使わず素のApiClientで送ること。
 * セッション同期の失敗はcallWithAuthの事前認証の前提が満たせない状態なので、
 * ApiClientWithAuth経由ではこのログ自体が送信できない（実行中のsyncを再帰的に待つことにもなる）。
 */
const reportErrorToLoggingApi = (message: string): void => {
  Promise.resolve(ApiClient.postJson('/api/logging', { logs: [{ level: 'error', message }] }))
    .catch(() => { /* ログ送信の失敗時にできることはない */ })
}

/**
 * 正しいセッションがcookieに存在する状態に同期する処理
 * - cookieが存在する
 * - cookieの情報がSSOT（技術的にはRedisのレコード）と等しい
 *
 * ただし、重複実行を避けるために成功結果をキャッシュしている
 * 現在のcookieが同期成功時と異なる場合はキャッシュを破棄して再実行する
 */
export class SyncSession {
  static exec(): Promise<void> {
    return SyncSession.getInstance().exec()
  }

  static onSynced<T>(callback: () => Promise<T>): Promise<T> {
    return SyncSession.getInstance().onSynced(callback)
  }

  static getInstance(): SyncSession {
    if (!SyncSession.instance) {
      SyncSession.instance = new SyncSession()
    }

    return SyncSession.instance
  }

  private static instance: SyncSession

  private syncPromise: Promise<void> | null = null
  private syncedSessionCacheKey: string | null = null

  private constructor(
    private readonly sessionApiService: SessionApiService = new SessionApiService(),
    private readonly sessionCookie: SessionCookie = new SessionCookie(),
    private readonly wait: (ms: number) => Promise<void> = waitFor,
    private readonly reportError: (message: string) => void = reportErrorToLoggingApi
  ) {}

  exec(): Promise<void> {
    this.invalidateCacheIfCookieChanged()

    if (this.syncPromise) {
      return this.syncPromise
    }

    this.syncPromise = this.innerSync()
      .then((payload) => {
        this.syncedSessionCacheKey = this.toSessionCacheKey(payload)
        log.debug({ service: `${namespace}/sync`, payload })

        return Promise.resolve()
      })

    return this.syncPromise
  }

  onSynced<T>(callback: () => Promise<T>): Promise<T> {
    return this.exec().then(callback)
  }

  /**
   * - セッションcookieが存在する（client側でパースできる範囲で正しいと思われる）
   *   - keepCurrentする
   *   - keepCurrentが正常に完了すれば成功
   *   - keepCurrentがセッション不正のエラーを返した場合、セッションが存在しないルートを実行する
   *   - keepCurrentがそれ以外のエラーを返した場合、Promiseをrejectする
   * - セッションcookieが存在しない
   *   - newする
   *   - newが正常に完了すれば成功
   *   - newが正常に完了しなければPromiseをrejectする
   */
  private async innerSync(): Promise<SessionCookiePayload> {
    const maybePayload = this.sessionCookie.readPayload()
    if (maybePayload) {
      const keepResult = await this.sessionApiService.keepCurrent()

      return keepResult.fold(
        (error: SessionApiErrorResponse) => {
          if (error.errorCode === 'invalid_cookie' || error.errorCode === 'not_found') {
            return this.new()
          }

          throw new Error(`${error.errorCode}: ${error.message}`)
        },
        () => {
          // keepCurrentに成功したのでcookieは存在するはず
          // 反映ラグを考慮してリトライし、それでも存在しなければrejectする
          return this.readPayloadOrFailWithRetry()
        }
      )
    }

    return this.new()
  }

  private async new(): Promise<SessionCookiePayload> {
    const isHlApp = HlAppMode.isPageForHlApp || HlAppMode.isViewedInHlAppWebView
    const result = await this.sessionApiService.new({
      isHlApp,
    })

    return result.fold(
      (error: SessionApiErrorResponse) => {
        throw new Error(`${error.errorCode}: ${error.message}`)
      },
      // newに成功したのでcookieは存在するはず
      // 反映ラグを考慮してリトライし、それでも存在しなければrejectする
      () => this.readPayloadOrFailWithRetry()
    )
  }

  /**
   * new/keepの成功直後にセッションcookieを読む。読めない場合は反映を待ちながらリトライする。
   *
   * ブラウザによっては、XHRレスポンスのSet-Cookieがdocument.cookieから読めるようになるまでラグがあり、
   * cookie自体は保存されているのにAPI成功直後の読み取りだけが失敗することがある（iOS Safariで再現）。
   * @see https://github.com/Everforth/growthpalette-front/issues/9094
   */
  private async readPayloadOrFailWithRetry(): Promise<SessionCookiePayload> {
    const startedAt = Date.now()

    let retryCount = 0
    while (true) {
      try {
        const payload = this.sessionCookie.readPayloadOrFail()

        // retryで読み取れるようになった場合のみログを出力
        if (retryCount > 0) {
          log.warn(
            `${namespace}: session cookie became readable` +
            ` (waited ${Date.now() - startedAt}ms, retry ${retryCount}/${READ_PAYLOAD_RETRY_LIMIT})`
          )
        }

        return payload
      } catch (error) {
        log.warn(
          `${namespace}: session cookie is not readable yet` +
          ` (waited ${Date.now() - startedAt}ms, retry ${retryCount}/${READ_PAYLOAD_RETRY_LIMIT})`
        )

        // リトライ上限を超過したら、
        // - logging APIにエラーを報告
        // - readPayloadOrFailのエラーをそのまま投げる
        if (retryCount >= READ_PAYLOAD_RETRY_LIMIT) {
          this.reportError(
            `${namespace}: give up waiting for the session cookie to be readable` +
            ` (waited ${Date.now() - startedAt}ms, retry ${retryCount}/${READ_PAYLOAD_RETRY_LIMIT},` +
            ` ua=${window.navigator.userAgent})`
          )

          throw error
        }

        await this.wait(READ_PAYLOAD_RETRY_INTERVAL_MS)
        retryCount += 1
      }
    }
  }

  /**
   * 前回のsync完了時のcookieと現在のcookieで状態が異なっている場合、
   * syncPromiseのキャッシュを破棄し再実行可能な状態に遷移する。
   * cookieの同一性はpayloadから作成されるcache keyで判定する。
   */
  private invalidateCacheIfCookieChanged(): void {
    if (!this.syncPromise || !this.syncedSessionCacheKey) {
      return
    }

    const currentPayload = this.sessionCookie.readPayload()
    if (currentPayload && this.toSessionCacheKey(currentPayload) === this.syncedSessionCacheKey) {
      return
    }

    this.syncPromise = null
    this.syncedSessionCacheKey = null
  }

  private toSessionCacheKey(payload: SessionCookiePayload): string {
    return `${payload.id}:${payload.v}:${payload.startAt}`
  }
}
